import '../../../assets/css/biography.css';
import React, {useEffect, useRef, useState} from "react";
import {BiographyBookSampleData} from "../../sample/BiographyBookSampleData";

import {Link, useLocation, useNavigate} from "react-router-dom";
import useApiByAuthor from "./apis/useApiByAuthor";
import useApiByTitle from "./apis/useApiByTitle";

/**
 * 아카이브 > 회고록 > 메인
 */
const Biography = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [posts, setPosts] = useState([]); // 현재 화면에 보여질 전체 게시물
    const [filteredPosts, setFilteredPosts] = useState([]); // 검색된 게시물
    const [visiblePosts, setVisiblePosts] = useState([]); // 화면에 표시할 데이터
    const [page, setPage] = useState(1); // 현재 페이지
    const observerRef = useRef(null); // IntersectionObserver 참조용
    const resultRef = useRef(null); // 검색 결과 컨테이너 참조
    const dataPerPage = 10;

    const [searchTerm, setSearchTerm] = useState(""); // 검색어
    const [searchFilter, setSearchFilter] = useState("author"); // 검색 기준
    const [searchQuery, setSearchQuery] = useState(""); //검색어 표시 실시간 렌더링 방지
    const [isSearching, setIsSearching] = useState(false); // 검색 여부

    const {  fetchBiographyByAuthor } = useApiByAuthor();
    const {  fetchBiographyByTitle } = useApiByTitle();

    // 초기 데이터 로드
    useEffect(() => {
        //TODO 초기 목록 전용 api 적용 (/get_books_by_category_and_publish_year_range)
        setPosts(BiographyBookSampleData);
        setFilteredPosts(BiographyBookSampleData);
        setVisiblePosts(BiographyBookSampleData.slice(0, dataPerPage));
    }, []);

    // URL 파라미터에 있는 검색어와 검색 기준으로 상태 설정
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const term = params.get('search') || ''; // URL에서 'search' 파라미터 추출
        const filter = params.get('filter') || 'author'; // URL에서 'filter' 파라미터 추출

        setSearchTerm(term); // term을 state로 설정
        setSearchFilter(filter); // filter를 state로 설정

        if (term) {
            handleSearch(term, filter); // 검색어가 있으면 검색 실행
        } else {
            // 검색어가 없으면 기본 데이터 설정
            setPosts(BiographyBookSampleData);
            setFilteredPosts(BiographyBookSampleData);
            setVisiblePosts(BiographyBookSampleData.slice(0, dataPerPage));
        }
    }, [location.search]); // URL 파라미터가 변경될 때마다 실행


    // 무한 스크롤 데이터 로드
    const loadMorePosts = (targetPage) => {
        const startIndex = (targetPage - 1) * dataPerPage;
        const endIndex = startIndex + dataPerPage;

        const source = isSearching ? filteredPosts : posts;
        const morePosts = source.slice(startIndex, endIndex);

        if (morePosts.length > 0) {
            setVisiblePosts((prev) => [...prev, ...morePosts]);
        }
    };

    // IntersectionObserver 설정
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setPage((prevPage) => prevPage + 1); // 다음 페이지 로드
                }
            },
            {threshold: 1.0}
        );

        if (observerRef.current) {
            observer.observe(observerRef.current);
        }

        return () => {
            if (observer && observerRef.current) {
                observer.unobserve(observerRef.current);
            }
            observer.disconnect(); // 옵저버 완전 해제
            setPosts(new Set());
            setPage(1);
        };
    }, []);

    // 페이지 변경 시 데이터 로드
    useEffect(() => {
        if (page > 1) {
            loadMorePosts(page);
        }
    }, [page]);


    // 검색 기능
    const handleSearch = async (searchTerm, filter) => {
        setIsSearching(true);
        setSearchQuery(searchTerm); // 검색어를 상태로 설정

        let data = [];
        if (filter === "title") {
            data = await fetchBiographyByTitle(searchTerm); // 검색어 기반으로 데이터 호출
            setFilteredPosts(data); // 기존 titleData 활용
        } else if (filter === "author") {
            data = await fetchBiographyByAuthor(searchTerm);
            setFilteredPosts(data); // 기존 authorData 활용
        }

        // 검색 결과 상태 업데이트
        setVisiblePosts(data.slice(0, dataPerPage)); // 페이지 크기만큼 데이터 표시
        setPage(1); // 페이지 초기화

        // 검색 시 스크롤 초기화
        if (resultRef.current) {
            resultRef.current.scrollTop = 0;
        }

        // 검색어와 기준을 URL에 파라미터로 추가
        navigate(`?search=${searchTerm}&filter=${filter}`);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault(); // 폼 제출 방지
            handleSearch(searchTerm, searchFilter);
        }
    };

    return (
        <main id="contents">
            <div className="sub-content">
                <div className="biography-wrap">
                    <div className="cont-top">
                        <div className="w-set">
                            <div className="title-wrap">
                                <h2 className="title batang">회고록</h2>
                                <p className="text">
                                    과거와 현재를 이어주는 회고록을 들여다보며, <br/>
                                    우리네의 삶 속에서 겪었던 다양한 교육적 경험을 확인할 수 있습니다.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="cont-btm">
                        <div className="w-set">
                            <div className="list-wrap">
                                <div className="list-top">
                                    <div className="search-wrap">
                                        <form onSubmit={(e) => e.preventDefault()}>
                                            <div className="search-box">
                                                <div className="select-area">
                                                    <select
                                                        value={searchFilter} // 상태 값 연결
                                                        onChange={(e) => setSearchFilter(e.target.value)} // 선택 시 상태 업데이트
                                                    >
                                                        <option value="author">저자</option>
                                                        <option value="title">제목</option>
                                                    </select>
                                                </div>
                                                <div className="search-area">
                                                    <div className="input-wrap">
                                                        <label htmlFor="">
                                                            <input
                                                                type="text"
                                                                name="search"
                                                                placeholder="검색어를 입력해 주세요."
                                                                value={searchTerm}
                                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                                onKeyDown={handleKeyDown}
                                                            />
                                                        </label>
                                                        <button type="button" className="btn-search" onClick={() => handleSearch(searchTerm, searchFilter)}>
                                                            검색
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                                <div className="list-btm">
                                    <p className="result-text">
                                        {isSearching ? (
                                            <>
                                                &#8242;<span>{searchQuery}</span>&#8242; 로 검색하신 결과는 <em>총 {filteredPosts.length}건</em> 입니다.
                                            </>
                                        ) : (
                                            <>
                                                총 <em>{posts.length}</em>건
                                            </>
                                        )}
                                    </p>
                                    <div className="table-wrap" ref={resultRef}>
                                        {visiblePosts.length !== 0 ? (
                                            <p>결과 없음</p>
                                        ) : (
                                            visiblePosts.map((item, index) => (
                                                <dl className="list-row" key={index}>
                                                    <dt>
                                                        <Link to={`/archive/biography/${item.book_id}`}>
                                                            <div className="list-info">
                                                                <p>{item.authors}</p>
                                                                <p className="title">{item.book_title}</p>
                                                                <p>{item.publisher}</p>
                                                                <p>{item.publish_date}</p>
                                                                <p className="office">
                                                                    {item.library}
                                                                </p>
                                                            </div>
                                                        </Link>
                                                    </dt>
                                                </dl>
                                            ))
                                        )}
                                        <div ref={observerRef}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default Biography;
